<script setup lang="ts">
import { useLicenseStore } from '@proj-airi/stage-ui/stores/license'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

import { getDefaultDeviceName } from '../utils/device'
import { generateFingerprint } from '../utils/fingerprint'

const licenseStore = useLicenseStore()
const { isActivated, isValidating, isActivating, error, onlineDeviceNames } = storeToRefs(licenseStore)

const purchaseUrl = import.meta.env.VITE_PURCHASE_URL || 'https://pay.ldxp.cn/item/mwh8sz'

const code = ref('')
const deviceName = ref('')
const defaultDeviceName = getDefaultDeviceName()
const isRetrying = ref(false)

const showGate = computed(() => {
  if (isActivated.value && isValidating.value)
    return false
  return !isActivated.value || !!error.value
})

/** 已有 token 或有 reauth 凭据，但遇到可重试的错误（并发超限、网络异常） */
const canRetry = computed(() =>
  (isActivated.value || (licenseStore.fingerprint && licenseStore.deviceSecret))
  && (error.value === 'concurrent_limit_exceeded' || error.value === 'network_error'),
)

const isTrialExpired = computed(() => error.value === 'trial_expired')

const errorText = computed(() => {
  switch (error.value) {
    case 'code_invalid': return '激活码无效'
    case 'trial_expired': return '您的试用期已结束，请购买正式激活码或联系管理员转正继续使用'
    case 'license_disabled': return '该激活码已被禁用'
    case 'max_devices_reached': return '该激活码绑定设备已达上限'
    case 'concurrent_limit_exceeded':
      return onlineDeviceNames.value.length > 0
        ? `当前已有设备在线：${onlineDeviceNames.value.join('、')}，请先在其他设备退出后再试`
        : '当前在线设备已达上限，请先在其他设备退出后再试'
    case 'network_error': return '网络异常，请稍后重试'
    case 'activation_failed': return '激活失败，请稍后重试'
    default: return error.value ? `错误：${error.value}` : ''
  }
})

async function submit() {
  const trimmed = code.value.trim().toUpperCase()
  if (!trimmed)
    return
  const fp = await generateFingerprint()
  licenseStore.setFingerprint(fp)
  await licenseStore.activate(trimmed, fp, deviceName.value.trim() || defaultDeviceName)
}

async function retry() {
  isRetrying.value = true
  error.value = ''
  if (licenseStore.sessionToken) {
    await licenseStore.validate()
  }
  else if (licenseStore.fingerprint && licenseStore.deviceSecret) {
    const result = await licenseStore.reauth()
    if (result === 'network_error')
      error.value = 'network_error'
    // reauth 成功时无需额外处理：licenseStore.reauth() 内部会更新 sessionToken 和 isActivated，
    // showGate 的 computed 会自动响应变化并隐藏遮罩
  }
  isRetrying.value = false
}

onMounted(async () => {
  if (licenseStore.sessionToken) {
    await licenseStore.validate()
  }
  else if (licenseStore.fingerprint && licenseStore.deviceSecret) {
    const result = await licenseStore.reauth()
    if (result === 'network_error')
      error.value = 'network_error'
  }
})
</script>

<template>
  <slot v-if="!showGate" />

  <div v-else class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-6">
    <div class="max-w-md w-full rounded-xl bg-white p-6 shadow-lg dark:bg-neutral-900 dark:text-white">
      <!-- 试用到期 -->
      <template v-if="isTrialExpired">
        <h2 class="mb-2 text-xl font-bold">
          试用已结束
        </h2>
        <p class="mb-4 text-sm text-red-600">
          {{ errorText }}
        </p>

        <div class="space-y-3">
          <a
            :href="purchaseUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="block w-full rounded-lg bg-black px-3 py-2 text-center text-white dark:bg-white dark:text-black"
          >购买正式激活码</a>

          <p class="text-center text-sm text-gray-500">
            或输入新的激活码
          </p>

          <input
            v-model="code"
            class="w-full border rounded-lg px-3 py-2 dark:border-neutral-700 dark:bg-neutral-800"
            placeholder="MEOW-XXXX-XXXX"
            autocomplete="off"
            @keyup.enter="submit"
          >
          <input
            v-model="deviceName"
            class="w-full border rounded-lg px-3 py-2 dark:border-neutral-700 dark:bg-neutral-800"
            :placeholder="`设备名（默认：${defaultDeviceName}）`"
            autocomplete="off"
            @keyup.enter="submit"
          >
          <button
            class="w-full rounded-lg bg-neutral-700 px-3 py-2 text-white dark:bg-neutral-600 disabled:opacity-50"
            :disabled="isActivating || !code.trim()"
            @click="submit"
          >
            {{ isActivating ? '激活中...' : '使用新激活码' }}
          </button>
        </div>
      </template>

      <!-- 可重试状态：并发超限 / 网络异常 -->
      <template v-else-if="canRetry">
        <h2 class="mb-2 text-xl font-bold">
          暂时无法使用
        </h2>
        <p class="mb-4 text-sm text-red-600">
          {{ errorText }}
        </p>
        <button
          class="w-full rounded-lg bg-black px-3 py-2 text-white dark:bg-white dark:text-black disabled:opacity-50"
          :disabled="isRetrying"
          @click="retry"
        >
          {{ isRetrying ? '重试中...' : '重试' }}
        </button>
        <p class="mt-4 text-xs text-gray-500">
          提示：关闭浏览器约 2 分钟后会自动释放在线名额。
        </p>
      </template>

      <!-- 激活表单 -->
      <template v-else>
        <h2 class="mb-2 text-xl font-bold">
          需要激活码
        </h2>
        <p class="mb-2 text-sm text-gray-600 dark:text-gray-400">
          请输入你购买的激活码（示例：MEOW-XXXX-XXXX）
        </p>
        <p class="mb-4 text-sm">
          <a
            :href="purchaseUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-blue-500 underline dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
          >还没有激活码？点此购买</a>
        </p>

        <div class="space-y-3">
          <input
            v-model="code"
            class="w-full border rounded-lg px-3 py-2 dark:border-neutral-700 dark:bg-neutral-800"
            placeholder="MEOW-XXXX-XXXX"
            autocomplete="off"
            @keyup.enter="submit"
          >
          <input
            v-model="deviceName"
            class="w-full border rounded-lg px-3 py-2 dark:border-neutral-700 dark:bg-neutral-800"
            :placeholder="`设备名（默认：${defaultDeviceName}）`"
            autocomplete="off"
            @keyup.enter="submit"
          >

          <button
            class="w-full rounded-lg bg-black px-3 py-2 text-white dark:bg-white dark:text-black disabled:opacity-50"
            :disabled="isActivating || !code.trim()"
            @click="submit"
          >
            {{ isActivating ? '激活中...' : '激活' }}
          </button>

          <p v-if="errorText" class="text-sm text-red-600">
            {{ errorText }}
          </p>
        </div>

        <p class="mt-4 text-xs text-gray-500">
          提示：关闭浏览器约 2 分钟后会自动释放在线名额。
        </p>
      </template>
    </div>
  </div>
</template>
